import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, Stack, toaster} from "rsuite";
import BasicGrid from "../../../grid/BasicGrid";
import AddUserModal from "../../../modals/admin/users/AddUserModal";
import EditUserModal from "../../../modals/admin/users/EditUserModal";
import SuccessNotification from "../../../notifications/SuccessNotification";
import {
    USER_ADD_ERROR,
    USER_ADD_SUCCESS,
    USER_EDIT_ERROR,
    USER_EDIT_SUCCESS
} from "../../../../service/constants";
import ErrorNotification from "../../../notifications/ErrorNotification";
import {getUsers} from "../../../../service/adminService";

const defaultColumns = [
    {
        key: 'id',
        label: 'Id',
    },
    {
        key: 'email',
        label: 'Email',
        flexGrow: 2,
    },
    {
        key: 'lastName',
        label: 'Last Name',
    },
    {
        key: 'firstName',
        label: 'First Name',
    },
    {
        key: 'role',
        label: 'Role',
    },
    {
        key: 'clearance',
        label: 'Clearance',
    },
    {
        key: 'lastUpdated',
        label: 'Last Updated',
    },
];


const Users = () => {

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState({});

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const loadUsers = useCallback(() => {
        setLoading(true);
        getUsers().then((users) => {
            setUsers(users);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const setClickedUser = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedUser(rowData);
    },[]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={USER_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUsers();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={USER_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUsers]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={USER_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUsers();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={USER_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    },[loadUsers]);


    return useMemo(() => {
        return (
            <>
                <Stack spacing={2} direction='column' alignItems='flex-start'>
                    <p>Click to edit/delete user.</p>
                    <p>
                        <b>Warning:</b> Modifying a user's email address may render them unable to login.
                        Deleting a user will remove all associated resume data. Proceed with caution.
                    </p>
                    <Button appearance="primary"
                            onClick={() => setAddModalOpen(true)}
                            style={{marginBottom: '1rem'}}
                    >
                        Add User
                    </Button>
                </Stack>
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={users}
                    handleClick={setClickedUser}
                    exportTitle="admin-user-list"
                />
                <AddUserModal
                    open={addModalOpen}
                    handleClose={handleAddClose}
                />
                <EditUserModal
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    selectedUser={selectedUser}
                />
            </>
        );
    }, [
        addModalOpen,
        columns,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedUser,
        setClickedUser,
        users]
    );
};

export default Users;